import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Row, Col, Typography, Card, Statistic, Spin } from 'antd';
import {
  BankOutlined,
  ApartmentOutlined,
  IdcardOutlined,
  AppstoreOutlined,
  ContactsOutlined,
  TeamOutlined,
  SolutionOutlined,
  CalculatorOutlined,
  FileTextOutlined,
  ScanOutlined,
  ScheduleOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import { useCompanyId } from '../../lib/books';
import request from '../../lib/request';
import { money } from '../../lib/format';

/**
 * ERPNext-workspace-style landing page: icon-grouped card grid under section
 * headers, mirroring the ADMIN/ERPSuite workspace layout from the earlier
 * ERPNext build - the single biggest lever for "looks like ERPNext" without
 * any of Frappe's actual complexity.
 */
function WorkspaceCard({ title, links }) {
  return (
    <div className="workspace-card">
      <Typography.Title level={5}>{title}</Typography.Title>
      {links.map((link) => (
        <div key={link.to} style={{ marginBottom: 8 }}>
          <Link to={link.to}>
            {link.icon} <span style={{ marginLeft: 8 }}>{link.label}</span>
          </Link>
        </div>
      ))}
    </div>
  );
}

/** One figure that needs attention, linking to where it is dealt with. */
function Tile({ title, value, suffix, to, warn }) {
  return (
    <Col xs={12} sm={8} lg={4}>
      <Link to={to}>
        <Card size="small" hoverable style={{ height: '100%' }}>
          <Statistic title={title} value={value} suffix={suffix} valueStyle={{ color: warn && value > 0 ? '#cf1322' : undefined, fontSize: 22 }} />
        </Card>
      </Link>
    </Col>
  );
}

export default function WorkspaceHome() {
  const auth = useAuth();
  const isSystemAdmin = auth.user?.role === 'SYSTEM_ADMIN';
  const { companyId } = useCompanyId();
  const [figures, setFigures] = React.useState(null);
  const [loadingFigures, setLoadingFigures] = React.useState(false);

  React.useEffect(() => {
    if (!companyId || auth.user?.role === 'CLIENT_ADMIN') return;
    setLoadingFigures(true);
    request
      .get('/reports/workspace', { company_id: companyId })
      .then((r) => r.json())
      .then((r) => setFigures(r.status ? r.data : null))
      .finally(() => setLoadingFigures(false));
  }, [companyId, auth.user]);

  // A client portal login has its own home page.
  if (auth.user?.role === 'CLIENT_ADMIN') return <Navigate to="/portal" replace />;

  return (
    <div>
      <Typography.Title level={3}>Welcome, {auth.user?.name}</Typography.Title>
      <Typography.Paragraph type="secondary">
        {auth.user?.companyName || 'InstantRead PMS'}
      </Typography.Paragraph>

      {loadingFigures && !figures ? (
        <Spin style={{ marginBottom: 16 }} />
      ) : (
        figures && (
          <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
            <Tile title="My open tasks" value={figures.my_open_tasks} to="/workbook" />
            <Tile title="Overdue tasks" value={figures.overdue_tasks} to="/tasks" warn />
            <Tile title="Deadlines in 14 days" value={figures.deadlines_due_14d} to="/deadlines" />
            <Tile title="Overdue deadlines" value={figures.deadlines_overdue} to="/deadlines" warn />
            <Tile title={`Outstanding invoices (${figures.invoices_outstanding.count})`} value={money(figures.invoices_outstanding.amount)} to="/invoices" />
            <Tile title={figures.timesheets.label} value={figures.timesheets.count} to="/timesheets" />
          </Row>
        )
      )}

      <Row gutter={[16, 16]}>
        {isSystemAdmin && (
          <Col xs={24} sm={12} md={8}>
            <WorkspaceCard
              title="ADMIN"
              links={[{ to: '/companies', icon: <BankOutlined />, label: 'Companies' }]}
            />
          </Col>
        )}
        <Col xs={24} sm={12} md={8}>
          <WorkspaceCard
            title="Organization"
            links={[
              { to: '/departments', icon: <ApartmentOutlined />, label: 'Departments' },
              { to: '/branches', icon: <BankOutlined />, label: 'Branches' },
              { to: '/designations', icon: <IdcardOutlined />, label: 'Career Levels' },
              { to: '/services', icon: <AppstoreOutlined />, label: 'Services' },
              { to: '/users', icon: <TeamOutlined />, label: 'Staff / Users' },
              { to: '/timesheets', icon: <ClockCircleOutlined />, label: 'Timesheets' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <WorkspaceCard
            title="CRM"
            links={[
              { to: '/leads', icon: <ContactsOutlined />, label: 'Leads' },
              { to: '/proposals', icon: <FileTextOutlined />, label: 'Proposals' },
              { to: '/associates', icon: <TeamOutlined />, label: 'Associates' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <WorkspaceCard
            title="Clients"
            links={[
              { to: '/clients', icon: <SolutionOutlined />, label: 'Clients' },
              { to: '/engagement-letters', icon: <FileTextOutlined />, label: 'Engagement Letters' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <WorkspaceCard
            title="Accounting"
            links={[
              { to: '/books', icon: <SolutionOutlined />, label: 'Client Books' },
              { to: '/counterparties', icon: <TeamOutlined />, label: 'Customers / Suppliers' },
              { to: '/journal', icon: <FileTextOutlined />, label: 'Transaction Posting' },
              { to: '/ledger-reports', icon: <CalculatorOutlined />, label: 'Ledger Reports (DL / TB / BS / P&L / FAR / Ageing)' },
              { to: '/fixed-assets', icon: <BankOutlined />, label: 'Fixed Assets' },
              { to: '/budgets', icon: <CalculatorOutlined />, label: 'Budgeting' },
              { to: '/chart-of-accounts', icon: <ApartmentOutlined />, label: 'Chart of Accounts' },
              { to: '/vat-setup', icon: <CalculatorOutlined />, label: 'VAT Setup' },
              { to: '/vat-reports', icon: <FileTextOutlined />, label: 'VAT / VIES / OSS / IOSS Reports' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <WorkspaceCard title="ScanNRead" links={[{ to: '/scan-n-read', icon: <ScanOutlined />, label: 'Documents' }]} />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <WorkspaceCard
            title="Workflow"
            links={[
              { to: '/workbook', icon: <AppstoreOutlined />, label: 'Workbook (work board)' },
              { to: '/engagements', icon: <ScheduleOutlined />, label: 'Service Engagements' },
              { to: '/projects', icon: <ScheduleOutlined />, label: 'Projects' },
              { to: '/tasks', icon: <CheckSquareOutlined />, label: 'Tasks' },
              { to: '/deadlines', icon: <CheckSquareOutlined />, label: 'Deadlines' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <WorkspaceCard
            title="Reports"
            links={[
              { to: '/reports', icon: <CalculatorOutlined />, label: 'Performance Analysis (utilisation, profitability, sales)' },
              { to: '/ledger-reports', icon: <FileTextOutlined />, label: 'Ledger Reports of the selected books' },
              { to: '/vat-reports', icon: <FileTextOutlined />, label: 'VAT / VIES / OSS / IOSS Reports' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <WorkspaceCard
            title="Billing"
            links={[
              { to: '/invoices', icon: <DollarOutlined />, label: 'Invoices' },
              { to: '/payments', icon: <CreditCardOutlined />, label: 'Payments / Receipts' },
              { to: '/credit-notes', icon: <FileTextOutlined />, label: 'Credit Notes' },
            ]}
          />
        </Col>
      </Row>
    </div>
  );
}
